import { observer } from 'mobx-react-lite'
import { useEffect, useState, type CSSProperties } from 'react'

import { formatAppDate } from '@/Ship/Helpers/appTime'
import { cx } from '@/Ship/Helpers/cssClass'

import { myShiftStore } from '../../Stores/MyShiftStore'
import { driverDashboardManager as store } from '../../Stores/DriverDashboardManager'
import { CompleteTaskModal } from '../AssignmentDetailDrawer'
import {
    getCargo,
    getCreatedAt,
    getCustomerName,
    getDispatcherName,
    getDispatcherPhone,
    getLoadingPoint,
    getRequestStatus,
    getTransportRequest,
    getUnloadingPoint,
    isRequestFrozen,
} from '../AssignmentDetailDrawer/Helpers'
import { RouteStrip } from '../RouteStrip/RouteStrip'
import { ConfirmStatusSheet } from './ConfirmStatusSheet'
import { TaskActionButtons } from './TaskActionButtons'
import { TASK_ACTIONS, TASK_STATUS_COLORS, TASK_STATUS_LABELS, type TaskAction } from './taskStatus'

import styles from './TaskCard.module.css'

import type { AssignmentItem } from '../AssignmentDetailDrawer'

interface TaskCardProps {
    assignment: AssignmentItem
    /** current — завдання, яке водій виконує зараз (жовта осьова маршруту) */
    variant?: 'current' | 'next'
}

/** «14.10, 14:00»; дата без часу → «14.10» */
const shortDateTime = (iso: string | null | undefined): string | null => formatAppDate(iso, 'DD.MM, HH:mm')

/**
 * Картка завдання в списку водія: номер і статус, вантаж, маршрут «звідки → куди»,
 * час, решта полів постановки і кнопка наступної дії. Клік по тілу відкриває деталі.
 */
export const TaskCard = observer(({ assignment: a, variant = 'next' }: TaskCardProps) => {
    const tr = getTransportRequest(a)
    const cargo = getCargo(a)
    const loadingPoint = getLoadingPoint(a)
    const unloadingPoint = getUnloadingPoint(a)
    const planned = shortDateTime(a.plannedLoadingDate)
    const created = shortDateTime(getCreatedAt(a))
    // Standalone-задачі несуть об'єкти самі, ERP-задачі — на заявці
    const constructionObject = a.constructionObject?.title || tr?.constructionObject?.title || null
    const productionObject = a.productionObject?.title || tr?.productionObject?.title || null
    const dispatcherPhone = getDispatcherPhone(a)
    const dispatcherName = getDispatcherName(a)
    const customerName = getCustomerName(a)

    const statusColor = TASK_STATUS_COLORS[a.status] || '#8c8c8c'
    const statusLabel = TASK_STATUS_LABELS[a.status] || a.status
    // Скасована/завершена заявка заморожує завдання (дзеркало серверної перевірки)
    const requestFrozen = isRequestFrozen(a)
    const frozenLabel = getRequestStatus(a) === 'completed' ? 'Заявку завершено' : 'Заявку скасовано'
    // Дії — лише із завданнями активної зміни (той самий ТЗ і кластер)
    const shiftBlock = requestFrozen ? null : myShiftStore.taskActionBlockReason(a)
    const actions = requestFrozen || shiftBlock ? [] : (TASK_ACTIONS[a.status] ?? [])

    const [completeOpen, setCompleteOpen] = useState(false)
    // Дія, що чекає підтвердження водія у шторці
    const [pending, setPending] = useState<TaskAction | null>(null)
    // Статус змінився ззовні (синхронізація, оновлення списку) — питання вже неактуальне
    useEffect(() => setPending(null), [a.status])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Завершення підтверджується в поп-апі оцінок, решта — у шторці підтвердження
    const run = (action: TaskAction) => {
        if (action.next === 'completed_by_driver') {
            setCompleteOpen(true)
            return
        }
        setPending(action)
    }

    const confirm = async (action: TaskAction, confirmedAt: string) => {
        setPending(null)
        if (loading) return
        setLoading(true)
        setError(null)
        try {
            await store.changeStatus(a.uuid, action.next, confirmedAt)
        } catch (err: any) {
            setError(err?.response?.message || err?.response?.detail || err?.message || 'Не вдалося змінити статус')
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <article
                className={`pwa-card ${cx(styles, 'card')}${variant === 'current' ? ` ${cx(styles, 'card--current')}` : ''} pwa-rise`}
                data-testid="task-card"
                data-status={a.status}
            >
                <div className={cx(styles, 'body')} onClick={() => store.openDrawer(a)} role="button" tabIndex={0}>
                    <div className={cx(styles, 'head')}>
                        <span className={cx(styles, 'number')}>
                            <small>#</small>
                            {a.id}
                        </span>
                        <span className={cx(styles, 'headSpacer')} />
                        <span
                            className="pwa-status pwa-status--ellipsis"
                            style={{ '--status': statusColor } as CSSProperties}
                        >
                            {statusLabel}
                        </span>
                    </div>

                    <h3 className={`${cx(styles, 'cargo')}${cargo ? '' : ` ${cx(styles, 'cargo--empty')}`}`}>
                        {cargo || 'Вантаж не вказано'}
                    </h3>

                    <RouteStrip
                        from={loadingPoint}
                        to={unloadingPoint}
                        tone={variant === 'current' ? 'marking' : 'default'}
                    />

                    <div className={cx(styles, 'when')}>
                        <div className={cx(styles, 'whenItem')}>
                            <span className="pwa-label">Завантаження</span>
                            <span className={cx(styles, 'whenValue')}>{planned ?? '—'}</span>
                        </div>
                        <div className={cx(styles, 'whenItem')}>
                            <span className="pwa-label">Створено</span>
                            <span className={`${cx(styles, 'whenValue')} ${cx(styles, 'whenValue--muted')}`}>
                                {created ?? '—'}
                            </span>
                        </div>
                    </div>

                    {(constructionObject || productionObject || dispatcherPhone || customerName || dispatcherName) && (
                        <div className={cx(styles, 'details')}>
                            {constructionObject && (
                                <div className={cx(styles, 'detail')}>
                                    <span className="pwa-label">Об'єкт будівництва</span>
                                    <span className={cx(styles, 'detailValue')}>{constructionObject}</span>
                                </div>
                            )}
                            {productionObject && (
                                <div className={cx(styles, 'detail')}>
                                    <span className="pwa-label">Об'єкт виробництва</span>
                                    <span className={cx(styles, 'detailValue')}>{productionObject}</span>
                                </div>
                            )}
                            {dispatcherPhone ? (
                                <div className={cx(styles, 'detail')}>
                                    <span className="pwa-label">Диспетчер</span>
                                    <a
                                        href={`tel:${dispatcherPhone}`}
                                        className={`${cx(styles, 'detailValue')} pwa-phone`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {dispatcherPhone}
                                    </a>
                                </div>
                            ) : (
                                dispatcherName && (
                                    <div className={cx(styles, 'detail')}>
                                        <span className="pwa-label">Диспетчер</span>
                                        <span className={cx(styles, 'detailValue')}>{dispatcherName}</span>
                                    </div>
                                )
                            )}
                            {customerName && (
                                <div className={cx(styles, 'detail')}>
                                    <span className="pwa-label">Відповідальний за замовлення</span>
                                    <span className={cx(styles, 'detailValue')}>{customerName}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {(error || requestFrozen || (shiftBlock && TASK_ACTIONS[a.status]?.length)) && (
                    <div className={cx(styles, 'notes')}>
                        {error && <div className="pwa-note pwa-note--danger">{error}</div>}
                        {requestFrozen && (
                            <div className="pwa-note pwa-note--danger">{frozenLabel} — виконання недоступне.</div>
                        )}
                        {!requestFrozen && shiftBlock && TASK_ACTIONS[a.status]?.length ? (
                            <div className="pwa-note pwa-note--warn">{shiftBlock}</div>
                        ) : null}
                    </div>
                )}

                <TaskActionButtons actions={actions} loading={loading} onRun={run} layout="flush" />
            </article>

            <ConfirmStatusSheet
                action={pending}
                taskId={a.id}
                currentStatus={a.status}
                onConfirm={confirm}
                onCancel={() => setPending(null)}
            />

            <CompleteTaskModal
                open={completeOpen}
                onClose={() => setCompleteOpen(false)}
                assignmentUuid={a.uuid}
                plannedLoadingDate={a.plannedLoadingDate}
                onCompleted={(uuid, newStatus) => {
                    store.updateAssignmentStatus(uuid, newStatus)
                    setCompleteOpen(false)
                }}
            />
        </>
    )
})
